import type {
  BlockEditResult,
  GenerationResult,
  ImageResult,
  StudioJob,
  StudioJobType,
  TranslationResult,
} from '../studioTypes'

// Пересечение, не объединение: у всех четырёх результатов все поля
// опциональные, поэтому любой конкретный `result` (и объединение
// `StudioJob['result']`) ему присваивается без `as`. Параметр `rawByType`
// типизирован ЭТИМ, а не `StudioJob['result']`, ровно чтобы функция могла
// читать любое из четырёх полей без сужения по `type` заново.
type AnyJobResult = GenerationResult & ImageResult & BlockEditResult & TranslationResult

type RawProgress = [done: number | undefined, total: number | undefined]

// Только выбор сырой пары done/total по типу и результату — fallback и
// дефолты применяются снаружи, ровно один раз, а не в каждой ветке.
const rawByType = (type: StudioJobType, result: AnyJobResult | null | undefined): RawProgress => {
  switch (type) {
    case 'course_generation':
      return [result?.lessons_done, result?.lessons_total]
    case 'course_image_generation':
      return [result?.images_done, result?.images_total]
    case 'course_block_edit':
      return [result?.blocks_done, result?.blocks_total]
    case 'course_translation':
      return [result?.translations_done, result?.translations_total]
    default: {
      // Недостижимо по типам: ветка существует ради рантайма (бэк может
      // прислать тип, которого нет в объединении) и ради компилятора —
      // появится пятый тип джобы, `never` перестанет сходиться, и сборка
      // упадёт здесь, а не отдаст молча нулевой прогресс.
      const _unhandled: never = type
      void _unhandled
      return [undefined, undefined]
    }
  }
}

/**
 * Числа прогресса по типу и результату — без самой джобы.
 *
 * Нужен снимку родителя (`JobEntry.previous`): у продолжения свой `result`, и
 * пересчитать числа прерванного прогона потом будет неоткуда — джоба родителя
 * в кеше не живёт. Считаем в момент снятия снимка.
 *
 * Фолбэка намеренно нет: снимок снимается с ТЕРМИНАЛЬНОЙ джобы, у которой
 * `result` уже есть; подставлять туда план со старта было бы подменой факта
 * обещанием.
 */
export const progressFromResult = (
  type: StudioJobType,
  result: StudioJob['result'],
): { done: number; total: number | null } => {
  const [done, total] = rawByType(type, result)
  return { done: done ?? 0, total: total ?? null }
}

/**
 * Прогресс прогона — единственное место, где он считается.
 *
 * Правила контракта (§1.3), которые иначе пришлось бы повторять в C, D, E и F:
 * знаменатель берётся из `*_total`, а НЕ из длины карты результата; всех трёх
 * ключей может не быть в `result` вовсе, поэтому чтение защитное; `*_total` по
 * ходу не меняется — это план, зафиксированный на старте; равенство
 * `done == total` при `success` НЕ гарантировано (штатный частичный результат);
 * по длине `events` прогресс не считается — число событий не равно числу единиц.
 *
 * `fallbackTotal` закрывает окно ДО первого отчёта воркфлоу: пока `result`
 * ещё `null`, число единиц уже известно из ответа на старт
 * (`targets_total`/`blocks_total`/`units_total`, см. `JobEntry.total`).
 * Приоритет всегда у `*_total` из `result` — план, зафиксированный на старте
 * джобы, не меняется задним числом подстановкой.
 */
export const readProgress = (
  job: StudioJob,
  fallbackTotal?: number | null,
): { done: number; total: number | null } => {
  const [done, total] = rawByType(job.type, job.result)
  return { done: done ?? 0, total: total ?? fallbackTotal ?? null }
}
